import React, { useState, useEffect } from "react";
import { Modal, Button, Row, Col, Spinner, Alert } from "react-bootstrap";
import { Form, Formik } from "formik";
import Select from "react-select";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

import FieldInput from "../../../components/global/form/FieldInput";
import TextLoading from "../../../components/global/TextLoading";
import selectStyles from "../../../components/global/GlobalTools";
import { GetTemplateWeights } from "../../../api/ReportCards";

// 0-100 with up to 2 decimals (reused pattern from ModalSubconcept)
const PERCENT_REGEX = /^(100(\.0{1,2})?|[1-9]?\d(\.\d{1,2})?)$/;

/**
 * Extracts an error message from the API envelope { status, data, error }.
 * A failure may surface at the top level (result.error) or nested
 * (result.data.status === false / result.data.error). Returns null on success.
 */
export const getWeightsResponseError = (result) => {
  if (!result) return "unknown";
  if (result.data && result.data.error) return result.data.error;
  if (result.data && result.data.status === false) return "unknown";
  if (result.status === false && result.error) {
    return typeof result.error === "string"
      ? result.error
      : result.error.description || "unknown";
  }
  if (result.error) {
    return typeof result.error === "string"
      ? result.error
      : result.error.description || "unknown";
  }
  return null;
};

/**
 * Modal editor for per-class weighting (%) inside a development area (CF).
 * Edits one CF at a time via a dropdown; validates that the sum equals 100%.
 * Gated behind the feature flag "partial_averages_in_development_areas".
 */
export default function CFWeightsModal(props) {
  const [t] = useTranslation(["reportCards", "global"]);
  const [selectedAreaId, setSelectedAreaId] = useState(null);
  const [weightValues, setWeightValues] = useState({});
  const [loadingWeights, setLoadingWeights] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const developmentAreas = props.developmentAreas || [];

  const currentArea = developmentAreas.find(
    (area) => area.development_area_id === selectedAreaId
  );
  const currentClasses = currentArea ? currentArea.classes || [] : [];

  // Set the first CF as selected when the modal opens.
  useEffect(() => {
    if (props.show && developmentAreas.length && selectedAreaId == null) {
      setSelectedAreaId(developmentAreas[0].development_area_id);
    }
    if (!props.show) {
      setSelectedAreaId(null);
      setWeightValues({});
      setSaving(false);
      setErrorMessage("");
    }
  }, [props.show]);

  // Load weights every time the modal opens or the CF changes.
  useEffect(() => {
    if (!props.show || selectedAreaId == null) return;
    setErrorMessage("");
    loadWeightsForArea(selectedAreaId);
  }, [selectedAreaId, props.show]);

  /**
   * Builds the initial percentage map for a CF.
   * Memory (props.cfWeights) wins; the API read is only a fallback.
   */
  const loadWeightsForArea = (areaId) => {
    const area = developmentAreas.find((a) => a.development_area_id === areaId);
    const classes = area ? area.classes || [] : [];

    // The in-memory store (props.cfWeights) is the source of truth for what will
    // be saved with the template. If the parent already holds weights for this CF
    // -whether hydrated from the DB on load/enable, or edited-but-not-yet-saved in
    // this session- use them and do NOT re-fetch: otherwise unsaved edits would be
    // discarded every time the modal reopens (QA report). New templates (no id)
    // also read straight from memory.
    const memoryWeights = props.cfWeights[areaId];
    if (memoryWeights || !props.templateId) {
      setWeightValues(buildValuesFromStore(classes, memoryWeights));
      return;
    }

    // No in-memory weights for this CF yet (e.g. a saved template whose all-areas
    // pre-fetch failed or has not run): fall back to a per-area DB read.
    setLoadingWeights(true);
    GetTemplateWeights(props.templateId, areaId)
      .then((result) => {
        // The API answers with HTTP 200 and a { status, data, error } envelope,
        // so a failure does not throw; inspect the envelope explicitly.
        const responseError = getWeightsResponseError(result);
        if (responseError) {
          setWeightValues(buildValuesFromStore(classes, props.cfWeights[areaId]));
          setErrorMessage(
            responseError === "unknown"
              ? t("reportCards:cfWeightsModal.loadError")
              : responseError
          );
          return;
        }
        const saved = {};
        const list = (result && result.data) || [];
        if (Array.isArray(list)) {
          list.forEach((item) => {
            saved[String(item.class_id)] =
              item.weight_percentage != null ? String(item.weight_percentage) : "";
          });
        }
        setWeightValues(buildValuesFromStore(classes, saved));
      })
      .catch(() => {
        // On error keep the form usable with whatever is in memory and warn.
        setWeightValues(buildValuesFromStore(classes, props.cfWeights[areaId]));
        setErrorMessage(t("reportCards:cfWeightsModal.loadError"));
      })
      .finally(() => setLoadingWeights(false));
  };

  /**
   * Builds a { class_id: value } map for the form, defaulting to empty string.
   */
  const buildValuesFromStore = (classes, store) => {
    const values = {};
    classes.forEach((singleClass) => {
      const classId = String(singleClass.value.value);
      values[classId] =
        store && store[classId] != null ? String(store[classId]) : "";
    });
    return values;
  };

  /**
   * Per-field validation: required + 0-100 with up to 2 decimals.
   * The exact "sum = 100" rule is validated in render (real-time alert).
   */
  const buildValidationSchema = (classes) => {
    const shape = {};
    classes.forEach((singleClass) => {
      const classId = String(singleClass.value.value);
      shape[classId] = Yup.string()
        .required(t("reportCards:cfWeightsModal.required"))
        .matches(PERCENT_REGEX, t("reportCards:cfWeightsModal.invalidValue"));
    });
    return Yup.object().shape(shape);
  };

  /**
   * Sum of the current percentages (rounded to 2 decimals to avoid float drift).
   */
  const sumWeights = (values) => {
    const total = Object.values(values || {}).reduce(
      (acc, value) => acc + (parseFloat(value) || 0),
      0
    );
    return Math.round(total * 100) / 100;
  };

  /**
   * Distributes 100% equally; the last class absorbs the rounding remainder.
   */
  const distributeEqually = (formik) => {
    const total = currentClasses.length;
    if (!total) return;
    const base = Math.floor((100 / total) * 100) / 100;
    const values = {};
    currentClasses.forEach((singleClass, index) => {
      const classId = String(singleClass.value.value);
      values[classId] =
        index === total - 1
          ? String(Math.round((100 - base * (total - 1)) * 100) / 100)
          : String(base);
    });
    formik.setValues(values);
  };

  const handleSave = async (values) => {
    const classes = currentClasses.map((singleClass) => ({
      class_id: singleClass.value.value,
      weight_percentage: parseFloat(values[String(singleClass.value.value)]),
    }));
    setSaving(true);
    setErrorMessage("");
    const result = await props.onSaveCF(selectedAreaId, classes);
    setSaving(false);
    // On failure keep the modal open and show the warning banner.
    if (result && result.error) {
      setErrorMessage(result.error);
      return;
    }
    props.setShow(false);
  };

  const areaOptions = developmentAreas.map((area) => ({
    value: area.development_area_id,
    label: area.title,
  }));

  return (
    <Modal
      id="cfWeightsModal"
      show={props.show}
      size="lg"
      aria-labelledby="contained-modal-title-lg"
    >
      <Modal.Header className="flex-column align-items-start">
        <Modal.Title>{t("reportCards:cfWeightsModal.title")}</Modal.Title>
        <p className="text-muted mt-2 mb-0">
          {t("reportCards:cfWeightsModal.description")}
        </p>
      </Modal.Header>
      <Formik
        enableReinitialize
        initialValues={weightValues}
        validationSchema={buildValidationSchema(currentClasses)}
        onSubmit={handleSave}
      >
        {(formik) => {
          const total = sumWeights(formik.values);
          const sumValid = total === 100;
          return (
            <Form>
              <Modal.Body>
                {errorMessage && (
                  <Alert variant="warning">{errorMessage}</Alert>
                )}
                <Row className="mb-3">
                  <Col md={12}>
                    <label className="form-label">
                      {t("reportCards:cfWeightsModal.selectField")}
                    </label>
                    <Select
                      inputId="cfWeights-area-select"
                      classNamePrefix="select"
                      styles={selectStyles}
                      options={areaOptions}
                      isDisabled={loadingWeights || saving}
                      value={
                        areaOptions.find(
                          (option) => option.value === selectedAreaId
                        ) || null
                      }
                      onChange={(option) =>
                        setSelectedAreaId(option ? option.value : null)
                      }
                    />
                  </Col>
                </Row>

                {loadingWeights ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" role="status" size="sm" />
                    <span className="ms-2">
                      {t("reportCards:cfWeightsModal.loading")}
                    </span>
                  </div>
                ) : (
                  <>
                    <h6 className="fw-bold mb-3">
                      {t("reportCards:cfWeightsModal.classes")}
                    </h6>
                    {currentClasses.map((singleClass, index) => {
                      const classId = String(singleClass.value.value);
                      // Sequential, DB-independent id so automated tests stay
                      // stable across environments (QA: ids must not derive from
                      // DB class_id). The form field name still uses class_id as
                      // the data key consumed on save/validation.
                      return (
                        <div key={classId} className="border rounded p-3 mb-2">
                          <Row className="align-items-center">
                            <Col md={6}>{singleClass.value.label}</Col>
                            <Col md={6}>
                              <FieldInput
                                id={"cfWeight-" + (index + 1)}
                                name={classId}
                                type="text"
                                label={t(
                                  "reportCards:cfWeightsModal.percentageLabel"
                                )}
                                placeholder="0"
                                disabled={saving}
                              />
                            </Col>
                          </Row>
                        </div>
                      );
                    })}

                    {currentClasses.length > 0 && (
                      <p
                        id="cfWeightsSumLabel"
                        className={
                          "mt-2 mb-0 " +
                          (sumValid ? "text-success" : "text-danger")
                        }
                      >
                        {t("reportCards:cfWeightsModal.sumLabel", { total })}
                      </p>
                    )}
                  </>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button
                  id="cfWeights-distribute"
                  variant="outline-secondary"
                  className="float-start"
                  disabled={loadingWeights || saving || !currentClasses.length}
                  onClick={() => distributeEqually(formik)}
                >
                  {t("reportCards:cfWeightsModal.distributeEqually")}
                </Button>
                <Button
                  id="cfWeights-cancel"
                  variant="outline-secondary"
                  disabled={saving}
                  onClick={() => props.setShow(false)}
                >
                  {t("global:buttons.cancel")}
                </Button>
                <Button
                  id="cfWeights-save"
                  variant="primary"
                  type="submit"
                  disabled={
                    loadingWeights || saving || !formik.isValid || !sumValid
                  }
                >
                  {saving ? (
                    <TextLoading
                      text={t("global:buttons.saving")}
                      variant="light"
                    />
                  ) : (
                    t("global:buttons.save")
                  )}
                </Button>
              </Modal.Footer>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
}

CFWeightsModal.propTypes = {
  show: PropTypes.bool,
  setShow: PropTypes.func,
  developmentAreas: PropTypes.array,
  cfWeights: PropTypes.object,
  onSaveCF: PropTypes.func,
  templateId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
